import { store } from '@/firebase'
import {
  CollectionName,
  formatDateWithDayName,
  formatHumanFullDate,
  FSStripeTeamSubscription,
  FSTeamPlan,
  TEAM_PLAN_PRICE
} from '@WombatTechnology/nitte-time-lib'
import { differenceInDays, isAfter } from 'date-fns'
import { isNull } from 'es-toolkit/compat'
import {
  collection,
  CollectionReference,
  doc,
  DocumentData,
  DocumentReference,
  getDoc,
  Timestamp
} from 'firebase/firestore'
import { toData } from './toData'

type PlanDoc = FSTeamPlan<
  Timestamp,
  DocumentReference<FSStripeTeamSubscription<Timestamp>>
>
export class TeamPlan {
  constructor(
    readonly ref: DocumentReference<PlanDoc>,
    readonly data: PlanDoc,
    readonly subscriptionData:
      | FSStripeTeamSubscription<Timestamp>
      | null
      | undefined
  ) {}

  static async build(id: string) {
    const ref = TeamPlan.doc(id)
    const data = await toData(TeamPlan.doc(id))
    const subscriptionData = await this.fetchSubscriptionData(
      data.subscriptionRef
    )
    return new TeamPlan(ref, data, subscriptionData)
  }

  private static async fetchSubscriptionData(
    subscriptionRef: DocumentReference<
      FSStripeTeamSubscription<Timestamp>
    > | null
  ) {
    if (!subscriptionRef) return null
    const subscription = await getDoc(subscriptionRef)
    return subscription.data() as
      | FSStripeTeamSubscription<Timestamp>
      | undefined
  }

  static doc(teamId: string) {
    return doc<PlanDoc, DocumentData>(this.collection(), teamId)
  }

  static collection() {
    return collection(
      store,
      `${CollectionName.TeamPlans}`
    ) as CollectionReference<PlanDoc>
  }

  get isTrialExpired() {
    if (
      isNull(this.freeTrialExpiredAt) ||
      this.isRegistered ||
      this.isServiceFree
    ) {
      return false
    }
    return isAfter(new Date(), this.freeTrialExpiredAt)
  }

  get trialExpiredInDays() {
    if (
      isNull(this.freeTrialExpiredAt) ||
      this.isRegistered ||
      this.isServiceFree
    ) {
      return false
    }
    return differenceInDays(this.freeTrialExpiredAt, new Date())
  }

  get isFreeTrial() {
    return this.data.status === 'freeTrial'
  }

  get isRegistered() {
    return this.data.status === 'registered'
  }

  get isServiceFree() {
    return this.data.status === 'serviceFree'
  }

  get freeTrialExpiredAt() {
    return this.data.freeTrialExpireAt.toDate()
  }

  get paymentMethod() {
    return this.data.paymentMethod
  }

  get isPaymentMethodLegacyInvoice() {
    return this.data.paymentMethod === 'legacyInvoice'
  }

  get isPaymentMethodCreditCard() {
    return this.data.paymentMethod === 'creditCard'
  }

  get freeTrialExpiredAtLabel(): string | null {
    if (isNull(this.freeTrialExpiredAt)) {
      return null
    }
    return formatDateWithDayName(this.freeTrialExpiredAt)
  }

  get isCanceled() {
    return !!this.subscriptionData?.cancel_at
  }

  /**
   * キャンセルされていないクレカのサブスクがあるか
   */
  get hasActiveSubscription() {
    return (
      !this.isCanceled && this.isRegistered && this.isPaymentMethodCreditCard
    )
  }

  get canceledAtFormatted() {
    return formatHumanFullDate(this.subscriptionData!.cancel_at!.toDate())
  }

  get pricePerUser(): number {
    return this.data.volumeDiscountPrice ?? TEAM_PLAN_PRICE
  }

  /**
   * サブスクリプションを支払ってるユーザーのuid
   */
  get paymendAdminUid(): string | null {
    if (isNull(this.data.subscriptionRef)) return null
    return this.data.subscriptionRef.parent?.parent?.id ?? null
  }
}
